/**
 * @voltxampere/engine — I2C bus DC stamp (Sprint 48-2)
 *
 * Per ADR-0069 — Engine extension #5. The `i2c-bus` canonical type is
 * a topological connector node: it carries no MNA contribution of its
 * own (no augmented row, no admittance entries). The bus state
 * machine (5-phase IDLE → START → ADDR → DATA → STOP) lives in a
 * separate helper module (`./i2c-bus-fsm.ts`) which the test runner
 * and Sprint 48-7 sensor activation invoke directly between solves —
 * mirroring the Sprint 47-2 mcu-execution stamp pattern (DC stamp is
 * read-only; instructions/transactions execute outside the Newton
 * loop).
 *
 * Sprint 48-2 MVP scope (Şefim onaylı plan §3 Sprint 48):
 *
 *   - DC stamp is no-op (no Y/B/C/rhs contribution). Sprint 49+ adds
 *     real-time edge detection + open-drain Norton stamps when sensor
 *     I2C ISR-driven traffic lands.
 *   - Schema validates SDA/SCL/VCC/GND pin layout; sentinel runner
 *     constructs an `I2cBusFsm` instance + verifies the 5-phase
 *     transition trace (Sprint 48-6 `sprint-48-i2c-master-slave-
 *     handshake.test.ts`).
 *   - Engine zero-diff: BROKEN (additive new file). 22 numerical + 3
 *     MCU sentinel bit-identical because the bus stamp is no-op DC
 *     contribution-wise — only stamp registry size grows.
 *
 * Out of scope (Sprint 50+):
 *
 *   - Real edge detection across Newton iterations (transient
 *     simulation dependent)
 *   - Clock stretching (slave holds SCL low)
 *   - Multi-master arbitration
 *   - Standard mode 100kHz / Fast mode 400kHz timing fidelity
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { defineDCStamp } from "./definition.js";
/**
 * The I2C bus stamp itself — no-op DC contribution. Topological
 * routing only. Sprint 49+ may add Norton current stamps for the
 * pull-up resistors when full transient bus simulation lands; until
 * then, slaves and masters drive their own pin voltages and the bus
 * is a passive net.
 */
export const i2cBusDCStamp = (_ctx, _component) => {
    // No MNA contribution. The bus is topologically passive — slave +
    // master devices apply their own stamps to SDA/SCL nets. The bus
    // FSM helper (`I2cBusFsm`) is consulted by the test runner /
    // sensor activation layer between solves.
};
/**
 * I2C bus stamp definition. No augmentation (no `["vsource"]` row),
 * no junction terminals (digital-only). The planner registers the
 * type so that placing an `i2c-bus` component does not raise
 * "no stamp registered" — a Sprint 49+ activation upgrade only
 * touches the stamp body, not the registration metadata.
 */
export const i2cBusDCStampDefinition = defineDCStamp("i2c-bus", i2cBusDCStamp, []);
//# sourceMappingURL=i2c-bus.js.map